// ===========================================
// Office access on Edit Teacher (roles slice 3, plan §7.3)
// ===========================================
// None · a staff role · the owner tier (Admin, shown by the school's owner title), moved in ONE step
// through `PUT /admin/roles/users/:userId/office-access`. It replaces the "Grant admin access"
// checkbox and its two-step move (add the staff role, then untick). Owner only: the page renders it
// for the owner tier, and the server's route is `ownerOnly`. It applies on its own Change button,
// after a confirmation, never with the form's Update.

import { useMemo, useState } from 'react';
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import ConfirmDialog from '../common/ConfirmDialog';
import { useCustomRoles, useSetOfficeAccess } from '../../hooks/useCustomRoles';
import { useTeacherAvailability } from '../../hooks/useUsers';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';
import type { Teacher } from '../../api/users.api';
import type { OfficeAccessInput } from '../../api/customRole.api';

const NONE = 'NONE';
const ADMIN = 'ADMIN';
const roleValue = (id: string) => `role:${id}`;

interface OfficeAccessSectionProps {
  teacher: Teacher;
  /** The signed-in owner editing themselves: their own Admin access cannot be removed. */
  isSelf: boolean;
}

export default function OfficeAccessSection({ teacher, isSelf }: OfficeAccessSectionProps) {
  const ownerTitle = useOwnerTitle();
  const { data: roles, isLoading: rolesLoading, isError: rolesError } = useCustomRoles();
  const setOfficeAccess = useSetOfficeAccess();

  // The page holds a copy of the teacher from when Edit opened; after a move, what was applied is
  // the truth until the list refetches and the dialog reopens.
  const [applied, setApplied] = useState<string | null>(null);
  const stored = teacher.User.role === 'ADMIN' ? ADMIN : teacher.staffRole ? roleValue(teacher.staffRole.id) : NONE;
  const current = applied ?? stored;
  const isAdmin = current === ADMIN;
  const [choice, setChoice] = useState(stored);
  const [confirming, setConfirming] = useState(false);

  // Leaving the owner tier makes them a teacher login again: one with no weekly availability is
  // offered to parents for make-ups on weekdays 9 to 5 (roles plan §7.3, L13).
  const leavingAdmin = isAdmin && choice !== ADMIN;
  const { data: availability } = useTeacherAvailability(leavingAdmin ? teacher.id : null);
  const noAvailability = leavingAdmin && availability !== undefined && !availability.some((s) => s.isActive);

  const name = `${teacher.User.firstName} ${teacher.User.lastName}`;
  const chosenRole = useMemo(
    () => roles?.find((r) => roleValue(r.id) === choice) ?? null,
    [roles, choice]
  );
  const describe = (value: string) =>
    value === ADMIN ? ownerTitle : value === NONE ? 'no office access' : roles?.find((r) => roleValue(r.id) === value)?.name ?? 'a staff role';

  const message = (() => {
    if (choice === ADMIN) {
      return `Give ${name} ${ownerTitle} access? They will be able to change everything, including billing, ` +
        `roles and other ${ownerTitle} logins.${current !== NONE ? ' Their staff role is removed.' : ''}`;
    }
    const from = describe(current);
    const to = describe(choice);
    const login = isAdmin ? ' Their login keeps working as a teacher.' : '';
    return `Change ${name} from ${from} to ${to}?${login} Enquiries they own go back to their group if they can no longer open enquiries.` +
      (noAvailability
        ? ' They have no weekly availability, so parents will be offered make-ups with them on weekdays 9 to 5 until it is entered.'
        : '');
  })();

  const input: OfficeAccessInput =
    choice === ADMIN ? { level: 'ADMIN' } : choice === NONE ? { level: 'NONE' } : { level: 'STAFF', customRoleId: chosenRole?.id };

  const lockedSelf = isSelf && isAdmin;

  return (
    <Box sx={{ mt: 2 }} data-testid="office-access">
      <Typography variant="subtitle2" gutterBottom>
        Office access
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 240, flex: 1 }} disabled={lockedSelf || rolesLoading || setOfficeAccess.isPending}>
          <InputLabel id="office-access-label">Office access</InputLabel>
          <Select
            labelId="office-access-label"
            label="Office access"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
          >
            <MenuItem value={NONE}>None (teacher only)</MenuItem>
            {(roles ?? []).map((r) => (
              <MenuItem key={r.id} value={roleValue(r.id)}>
                {r.name}
                {/* A name the server already suffixed ("Staff (default)") is not marked twice (/qa L3). */}
                {r.isSystem && !/\(default( \d+)?\)$/.test(r.name) ? ' (default)' : ''}
              </MenuItem>
            ))}
            <MenuItem value={ADMIN}>{ownerTitle}</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          disabled={lockedSelf || choice === current || setOfficeAccess.isPending || (input.level === 'STAFF' && !input.customRoleId)}
          onClick={() => setConfirming(true)}
        >
          Change
        </Button>
      </Stack>
      {rolesError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          The staff roles could not be loaded.
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {lockedSelf
          ? `You cannot remove your own ${ownerTitle} access.`
          : `${ownerTitle} access can change everything; a staff role gives the permissions it holds. ` +
            'The change applies straight away, separately from Update.'}
      </Typography>
      {noAvailability && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {teacher.User.firstName} has no weekly availability. As a teacher login, parents will be offered make-ups
          with them on weekdays 9 to 5 until availability is entered.
        </Alert>
      )}

      <ConfirmDialog
        open={confirming}
        title={choice === ADMIN ? `Give ${ownerTitle} access?` : 'Change office access?'}
        message={message}
        confirmLabel="Change access"
        confirmColor="warning"
        loading={setOfficeAccess.isPending}
        onConfirm={() =>
          setOfficeAccess.mutate(
            { userId: teacher.userId, data: input },
            { onSuccess: () => setApplied(choice), onSettled: () => setConfirming(false) }
          )
        }
        onCancel={() => setConfirming(false)}
      />
    </Box>
  );
}
